import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Select, Textarea } from '@ds/forms';
import { enviarParaVarios, resumirEnvio } from '@/comuns/mensagens';
import type { Destinatario } from '@/comuns/mensagens';
import { SeletorDeCanal } from '@/comuns/SeletorDeCanal';
import { useAviso } from '@/comuns/toast';

export interface MensagemEmLoteModalProps {
    open: boolean;
    destinatarios: Destinatario[];
    onClose: () => void;
}

const MODELOS = [
    { value: 'livre', label: 'Mensagem livre' },
    { value: 'retorno', label: 'Convite de retorno' },
    { value: 'promocao', label: 'Campanha do mês' },
];

const TEXTOS: Record<string, string> = {
    retorno: 'Oi, {nome}! Faz um tempo desde o seu último atendimento na Vecchia. Quer que eu veja um horário para você esta semana?',
    promocao: 'Oi, {nome}! Temos condição especial em pacotes até o fim do mês. Posso te enviar os valores?',
};

/**
 * Mensagem para vários contatos, enviada de verdade.
 *
 * O modal dizia "Mensagem enviada para N contatos" sem chamar nada. Agora cada
 * pessoa recebe pela conversa dela no canal escolhido, e o aviso final conta o
 * que saiu, o que ficou na fila do canal e o que falhou.
 */
export function MensagemEmLoteModal({ open, destinatarios, onClose }: MensagemEmLoteModalProps) {
    const aviso = useAviso();
    const idModelo = useId();
    const idTexto = useId();
    const [modelo, setModelo] = useState('livre');
    const [texto, setTexto] = useState('');
    const [canalId, setCanalId] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setModelo('livre');
        setTexto('');
    }, [open]);

    const enviar = async (): Promise<boolean> => {
        if (!canalId || !texto.trim()) return false;
        setEnviando(true);
        try {
            const resultados = await enviarParaVarios(destinatarios, canalId, texto.trim());
            const resumo = resumirEnvio(resultados);
            aviso({ title: resumo.titulo, description: resumo.descricao, tone: resumo.tom });
            onClose();
            return true;
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Modal
            open={open} onClose={onClose} width={520} title="Enviar mensagem"
            description={`${destinatarios.length} ${destinatarios.length === 1 ? 'destinatário' : 'destinatários'}`}
            dirty={texto.trim() !== ''} onSave={enviar} saveLabel="Enviar e fechar"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void enviar()} loading={enviando} disabled={!texto.trim() || !canalId}>Enviar</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* O envio em lote não é reversível: dizer para quem vai, antes do
                    clique, é o que evita o disparo errado. */}
                <Alert tone="neutral" title="Confira antes de enviar" dismissible={false}>
                    {destinatarios.map((d) => d.nome).join(', ')}
                </Alert>
                <SeletorDeCanal value={canalId} onChange={setCanalId} />
                <FormField label="Modelo" htmlFor={idModelo}>
                    <Select
                        id={idModelo} value={modelo} options={MODELOS}
                        onChange={(v) => { setModelo(v); setTexto(TEXTOS[v] ?? ''); }}
                    />
                </FormField>
                <FormField label="Mensagem" required htmlFor={idTexto} hint="{nome} vira o primeiro nome de cada pessoa">
                    <Textarea id={idTexto} value={texto} onChange={setTexto} rows={4} placeholder="Escreva a mensagem…" />
                </FormField>
            </div>
        </Modal>
    );
}
